import { useTranslation } from 'react-i18next';
import { confirmDialog, useModalStore } from '@noor/lib';
import { Can } from '@/entities/access';
import { RowActionMenu, RowActionMenuItem } from '@/shared/ui/row-action-menu';
import {
  canCancelOrder,
  canFinishStage,
  canReorderCourier,
  type OrderRow,
} from '@/entities/order';
import { useFinishOrder, useReorderCourier } from '../model/use-order-actions';

interface OrderRowActionsProps {
  order: OrderRow;
  /** Open the courier chat for this order. */
  onChat?: ((orderId: string | number) => void) | undefined;
  /**
   * The `orders.finish` permission. It gates BOTH the finish icon
   * (cpanel wrapped `CancelOrderBtn` in `CheckAccess`) and the chat icon
   * (`canFinishOrder`). Resolved by the composing widget — a feature can't
   * import the access-control feature.
   */
  canFinish?: boolean | undefined;
}

/**
 * Row action icons, cpanel `CreateOrderNoteCell`: note (slot) / re-order /
 * cancel / finish / chat. Re-order and cancel are stage-gated; finish is
 * stage-gated AND permission-gated; chat needs an assigned courier plus the
 * same permission. Re-order/finish confirm via the promise dialog (closing on
 * failure too, as cpanel did); cancel opens the cancel-order modal.
 */
export function OrderRowActions({
  order,
  onChat,
  canFinish = false,
}: OrderRowActionsProps) {
  const { t } = useTranslation('orders');
  const openModal = useModalStore((s) => s.openModal);
  const finish = useFinishOrder();
  const reorder = useReorderCourier();

  const stage = order.stage;
  const number = order.display_id ?? order.id;
  const showReorder = canReorderCourier(stage, {
    vendorOrderId: order.vendor_order_id,
    serviceId: order.service_id,
  });
  const showCancel = canCancelOrder(stage);
  const showFinish = canFinishStage(stage) && canFinish;
  const showChat = Boolean(order.courier) && canFinish;

  const onReorder = async () => {
    await confirmDialog({
      title: t('confirm.reorderTitleNumber', { number }),
      description: t('confirm.reorderDescription'),
      confirmLabel: t('confirm.yes'),
      cancelLabel: t('confirm.no'),
      onConfirm: () => reorder.mutateAsync(order.id).catch(() => undefined),
    });
  };

  const onFinish = async () => {
    await confirmDialog({
      title: t('confirm.finishTitle', { number }),
      description: t('confirm.finishDescription', { number }),
      confirmLabel: t('confirm.yes'),
      cancelLabel: t('confirm.no'),
      onConfirm: () => finish.mutateAsync(order.id).catch(() => undefined),
    });
  };

  return (
    <RowActionMenu label={t('column.actions')}>
      <Can permission="orders.notes.create">
        <RowActionMenuItem
          action="create"
          onSelect={() => openModal('create-note', { orderId: order.id })}
        >
          {t('action.createNote')}
        </RowActionMenuItem>
      </Can>
      {showReorder && (
        <Can permission="couriers.manage_order">
          <RowActionMenuItem action="reorder" onSelect={() => void onReorder()}>
            {t('action.reorder')}
          </RowActionMenuItem>
        </Can>
      )}
      {showCancel && (
        <Can permission="orders.cancel">
          <RowActionMenuItem
            action="cancel"
            onSelect={() =>
              openModal('cancel-order', {
                orderId: order.id,
                ...(order.display_id != null ? { displayId: order.display_id } : {}),
                stage,
              })
            }
          >
            {t('action.cancel')}
          </RowActionMenuItem>
        </Can>
      )}
      {showFinish && (
        <RowActionMenuItem action="finish" onSelect={() => void onFinish()}>
          {t('action.finish')}
        </RowActionMenuItem>
      )}
      {showChat && onChat && (
        <RowActionMenuItem action="chat" onSelect={() => onChat(order.id)}>
          {t('action.chat')}
        </RowActionMenuItem>
      )}
    </RowActionMenu>
  );
}
